import React, { useCallback } from 'react';
import {
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalFooterButtons,
  ModalHeader,
  Text,
  useToast,
} from '@televet/kibble-ui';
import { useUpdateOneClinicPetMutation } from 'shared/types/graphql';

interface DeceasedPetModalProps {
  isCurrentlyDeceased?: boolean;
  petId?: string | null;
  isOpen: boolean;
  handleClose: () => void;
}

const DeceasedPetModal = ({ isCurrentlyDeceased, isOpen, petId, handleClose }: DeceasedPetModalProps): JSX.Element => {
  const [updateClinicPet] = useUpdateOneClinicPetMutation();
  const toast = useToast();

  const handleSubmit = useCallback(async () => {
    if (!petId) return;
    const today = new Date();

    const data = {
      isDeceased: !isCurrentlyDeceased,
      isActive: false,
      dateOfDeath: !isCurrentlyDeceased ? today.toISOString() : null,
    };

    try {
      await updateClinicPet({
        variables: {
          where: {
            id: petId,
          },
          data,
        },
      });
      handleClose();
    } catch (e) {
      toast({
        title: 'Error!',
        status: 'error',
        description: 'There was an error when attempting to update this pet. Please try again.',
      });
    }
  }, [isCurrentlyDeceased, toast, petId, updateClinicPet, handleClose]);

  return (
    <Modal isOpen={isOpen} onClose={handleClose}>
      <ModalHeader>Mark patient as {isCurrentlyDeceased ? 'living' : 'deceased'}</ModalHeader>
      <ModalCloseButton />
      <ModalBody>
        {isCurrentlyDeceased ? (
          <>
            <Text variant="warning" fontWeight="bold">
              This will not mark the patient as active.{' '}
            </Text>
            <Text>If you would like to activate this patient, please do so in the patient profile. </Text>
          </>
        ) : (
          <>
            <Text variant="warning" fontWeight="bold">
              Deceased patients become inactive.{' '}
            </Text>
            <Text>
              Inactive patients cannot be added to Appointments. Appointments associated with this patient will not
              trigger automations or follow-up surveys.
            </Text>
          </>
        )}
      </ModalBody>
      <ModalFooterButtons onSubmit={handleSubmit} submitText="Confirm" />
    </Modal>
  );
};

export default DeceasedPetModal;
